import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { api } from './authStore';
import { extractTenantSubdomain } from '@/utils/tenantHostname';

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  tenant: z.string().min(1, 'Tenant is required'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export const ForgotPasswordPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState('');
  const [isTenantAutoDetected, setIsTenantAutoDetected] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  // Auto-detect tenant from subdomain
  useEffect(() => {
    const subdomain = extractTenantSubdomain(window.location.hostname);

    if (subdomain) {
      setValue('tenant', subdomain);
      setIsTenantAutoDetected(true);
    } else {
      setIsTenantAutoDetected(false);
    }
  }, [setValue]);

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsLoading(true);
    
    try {
      const response = await api.post('/auth/forgot-password', {
        email: data.email,
        tenantSubdomain: data.tenant,
      });

      if (response.data.success) {
        setSubmittedEmail(data.email);
        setIsSuccess(true);
        toast.success('Password reset email sent successfully!');
      }
    } catch (error: any) {
      console.error('Forgot password error:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Failed to send password reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pastel-blue via-white to-pastel-green flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft-lg p-8 border border-white/20">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-3xl font-display font-bold text-primary-500 mb-2">
                Check Your Email
              </h1>
              <p className="text-gray-600 font-body">
                Password reset instructions sent
              </p>
            </div>

            {/* Success Message */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-6">
              <p className="text-green-800 text-center mb-4">
                We've sent password reset instructions to:
              </p>
              <p className="text-green-900 font-semibold text-center mb-4 break-all px-2">
                {submittedEmail}
              </p>
              <p className="text-green-700 text-sm text-center">
                Please check your email and follow the instructions to reset your password. 
                The link will expire in 24 hours.
              </p>
            </div>

            {/* Additional Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6">
              <p className="text-blue-800 text-sm text-center mb-2">
                <strong>Didn't receive the email?</strong>
              </p>
              <ul className="text-blue-700 text-sm space-y-1">
                <li>• Check your spam/junk folder</li>
                <li>• Make sure the email address is correct</li>
                <li>• Wait a few minutes for delivery</li>
              </ul>
            </div>

            {/* Actions */}
            <div className="space-y-4">
              <button
                onClick={() => {
                  setIsSuccess(false);
                  setSubmittedEmail('');
                }}
                className="w-full btn btn-secondary"
              >
                Try Different Email
              </button>
              
              <Link
                to="/login"
                className="w-full btn btn-primary flex items-center justify-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pastel-blue via-white to-pastel-green flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-soft-lg p-8 border border-white/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-primary-500" />
            </div>
            <h1 className="text-3xl font-display font-bold text-primary-500 mb-2">
              Forgot Password?
            </h1>
            <p className="text-gray-600 font-body">
              Enter your email to receive reset instructions
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Tenant Field */}
            <div className="form-group">
              <label htmlFor="tenant" className="form-label">
                Tenant / Firm
              </label>
              <input
                {...register('tenant')}
                type="text"
                id="tenant"
                className="input"
                placeholder="Enter your firm's subdomain"
                readOnly={isTenantAutoDetected}
              />
              {errors.tenant && (
                <p className="form-error">{errors.tenant.message}</p>
              )}
              {/* Show auto-detection hint */}
              {isTenantAutoDetected && (
                <p className="text-sm text-green-600 mt-1">
                  ✓ Tenant auto-detected from subdomain
                </p>
              )}
            </div>

            {/* Email Field */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                {...register('email')}
                type="email"
                id="email"
                className="input"
                placeholder="Enter your email address"
              />
              {errors.email && (
                <p className="form-error">{errors.email.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full btn btn-primary flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  Send Reset Instructions
                </>
              )}
            </button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 font-medium transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
